import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { CrmActionOutcomeLeaderboardItem } from "./use-crm-action-outcome-leaderboard";

export interface CrmActionTelemetryFilters {
  actionRef?: string;
  contractRef?: string;
  sourceEventType?: string;
  signalFamily?: string;
}

export interface CrmActionTelemetryDecisionTotals {
  accepted: number;
  dismissed: number;
  total: number;
}

export interface CrmActionTelemetryEffectiveness {
  reviewed: number;
  accepted: number;
  dismissed: number;
  materialized: number;
  crmMaterialized: number;
  manualResolved: number;
  acceptanceRate: number;
  materializationRate: number;
  crmMaterializationRate: number;
  manualResolutionRate: number;
  avgTimeToMaterializeMinutes: number | null;
  lastReviewedAt: string | null;
  lastAcceptedAt: string | null;
  lastMaterializedAt: string | null;
}

export interface CrmActionTelemetryQueue {
  accepted: number;
  materialized: number;
  readyForExecution: number;
  manualReview: number;
  blocked: number;
  unknown: number;
}

export interface CrmActionTelemetrySourceSurface extends CrmActionTelemetryEffectiveness {
  sourceSurface: string;
}

export interface CrmActionTelemetrySourceEventType extends CrmActionTelemetryEffectiveness {
  sourceEventType: string;
  signalFamily: string;
}

export interface CrmActionTelemetrySignalFamily extends CrmActionTelemetryEffectiveness {
  signalFamily: string;
}

export interface CrmActionTelemetryActorType extends CrmActionTelemetryEffectiveness {
  actorType: string;
}

export interface CrmActionTelemetry {
  windowDays: number;
  filters: {
    actionRef: string | null;
    contractRef: string | null;
    sourceEventType: string | null;
    signalFamily: string | null;
  };
  decisions: CrmActionTelemetryDecisionTotals;
  effectiveness: CrmActionTelemetryEffectiveness;
  queue: CrmActionTelemetryQueue;
  bySourceSurface: CrmActionTelemetrySourceSurface[];
  bySourceEventType: CrmActionTelemetrySourceEventType[];
  bySignalFamily: CrmActionTelemetrySignalFamily[];
  byActorType: CrmActionTelemetryActorType[];
  automation: CrmActionTelemetryEffectiveness;
  staff: CrmActionTelemetryEffectiveness;
  topOutcomes: CrmActionOutcomeLeaderboardItem[];
}

export function useCrmActionTelemetry(filters: CrmActionTelemetryFilters = {}) {
  const [data, setData] = useState<CrmActionTelemetry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTelemetry = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const actionRef = filters.actionRef ?? filters.contractRef;
      if (actionRef) {
        params.set("actionRef", actionRef);
      }
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmActionTelemetry }>(
        `/crm/intelligence/action-recommendations/telemetry${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load action telemetry");
    } finally {
      setIsLoading(false);
    }
  }, [filters.actionRef, filters.contractRef, filters.signalFamily, filters.sourceEventType]);

  useEffect(() => {
    fetchTelemetry();
  }, [fetchTelemetry]);

  return { data, isLoading, error, refetch: fetchTelemetry };
}
